import DefaultProductLogo from "@assets/images/logo/default-product-logo.png";
import {
  SHAvatar,
  SHChip,
  SHStack,
  SHTableCell,
  SHTypography,
} from "@components/design-systems";
import { GlobalProductsToHideSubProductName } from "@constants/products";
import { useUserPermissions } from "@hooks/userUserPermission";
import { SelectedSubProductDTO } from "@models/managed-accounts/entities/step/fee";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";
import { useTheme } from "@mui/material";
import { getProductRecommendationConfigs } from "@pages/platform-analysis-v2/_id/steps/summary/utils";
import { toSentence } from "@utils";
import { isNil } from "lodash";
import { memo, useMemo } from "react";
import { TableVirtualizationConfigs as Configs } from "../../../../configs";
import { getFeeSubProductStatusColor } from "../../../../utils";
import { PerformanceNumberCell } from "../../value-renders/performance-number-cell";

interface RenderedSubProductHeaderCellProps {
  subProduct: SelectedSubProductDTO;
  fixedWidth?: number;
}
export const RenderedSubProductHeaderCell = memo(
  ({
    subProduct,
    fixedWidth = Configs.DefaultColumnWidth,
  }: RenderedSubProductHeaderCellProps) => {
    const isFireFox =
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") !== -1;
    const { palette } = useTheme();
    const { isSupplierGroup } = useUserPermissions();
    const {
      productLogoUrl: productLogoData,
      productName,
      name: subProductName,
      isProductMain,
      totalFees,
      totalFeesExcPerf,
      totalFeesInPercent,
      totalFeesExcPerfInPercent,
      setupType,
      recommendation,
    } = subProduct || {};

    const productLogo = useMemo(() => {
      if (!productLogoData) return DefaultProductLogo;

      return productLogoData;
    }, [productLogoData]);

    const isHidingSubProductName = useMemo(() => {
      return (
        GlobalProductsToHideSubProductName.includes(productName || "") &&
        setupType === AnalysisFeePortfolioSetupType.SubProduct
      );
    }, [productName, setupType]);

    const renderMainProductBadge = () => {
      if (!isProductMain) return null;

      return (
        <SHChip
          size="small"
          className="ProductCell-main-badge sub-product"
          label={isSupplierGroup ? "Main" : "Current"}
          color={"warning"}
          variant={"outlined"}
          sx={{
            position: "absolute",
            zIndex: 1,
          }}
        />
      );
    };

    const renderCellContent = (isPseudo?: boolean) => {
      const { backgroundColor, icon, label, isCustomType } =
        getProductRecommendationConfigs(recommendation);

      return (
        <SHStack
          width={"100%"}
          height={"100%"}
          direction={"column"}
          justifyContent={"flex-start"}
        >
          <SHStack justifyContent={"space-between"} flexGrow={1}>
            <SHStack
              className="ProductCell-avatar-and-score sub-product"
              justifyContent={"space-between"}
              alignItems={"center"}
              flexGrow={1}
            >
              {/* Avatar & Name */}
              <SHStack
                className={`ProductCell-avatar`}
                gap={0.5}
                padding={"5px 5px !important"}
                flex={1}
              >
                <SHAvatar
                  className="ProductCell-avatar-image"
                  src={!isPseudo ? productLogo : undefined}
                  isCircular={false}
                />

                <SHTypography
                  className="ProductCell-avatar-name"
                  variant="subtitle1"
                  fontSize={14}
                  fontWeight={700}
                  lineHeight={"110%"}
                  sx={{
                    transition: "all 0.3s ease",
                    wordBreak: "normal",
                    overflowWrap: "break-word",
                    hyphens: "auto",
                  }}
                >
                  {productName}
                  <br />
                  {!isHidingSubProductName && subProductName}
                </SHTypography>
              </SHStack>

              {/* Score */}
              <SHStack
                className="ProductCell-score"
                direction={"column"}
                paddingX={"5px"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <SHTypography
                  className="ProductCell-score-title"
                  variant="body3"
                  colorVariant="third"
                  fontSize={13}
                  style={{ transition: "all 0.3s ease" }}
                >
                  Total
                </SHTypography>

                <PerformanceNumberCell
                  value={totalFees}
                  valueInPercent={totalFeesInPercent}
                  valueExcPerf={totalFeesExcPerf}
                  valueExcPerfInPercent={totalFeesExcPerfInPercent}
                  renderValue={(value: string) => (
                    <SHTypography
                      variant="subtitle1"
                      lineHeight={"150%"}
                      fontSize={20}
                      fontWeight={700}
                    >
                      {value}
                    </SHTypography>
                  )}
                />
              </SHStack>
            </SHStack>
          </SHStack>

          {!isNil(recommendation) && label && (
            <SHStack
              className="DecisionBanner"
              bgcolor={backgroundColor || "transparent"}
              marginTop={"0px !important"}
            >
              <SHStack
                className={`DecisionBanner-content ${isPseudo ? "pseudo" : ""}`}
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.5}
              >
                {icon}
                <SHTypography
                  variant="body3"
                  colorVariant="white"
                  paddingBottom="-2px"
                  fontSize="12px"
                >
                  {isCustomType ? label : toSentence(label)}
                </SHTypography>
              </SHStack>
            </SHStack>
          )}
        </SHStack>
      );
    };

    return (
      <SHTableCell
        sx={{
          position: "relative",
          padding: "0px !important",
          margin: "0px !important",
          textAlign: "center",
          minWidth: fixedWidth,
          maxWidth: fixedWidth,
          border: `1px solid ${palette.secondary[100]}`,
          borderLeftWidth: "0px",
          backgroundColor: getFeeSubProductStatusColor(),
        }}
      >
        {renderMainProductBadge()}

        {isFireFox && (
          <SHStack
            sx={{
              position: "absolute",
              height: "100%",
              width: "100%",
              top: 0,
              left: 0,
            }}
          >
            {renderCellContent()}
          </SHStack>
        )}

        <SHStack
          width={"100%"}
          height={"100%"}
          direction={"column"}
          justifyContent={"flex-start"}
          visibility={isFireFox ? "hidden" : "visible"}
          sx={{ pointerEvents: isFireFox ? "none" : "auto" }}
        >
          {renderCellContent(isFireFox)}
        </SHStack>
      </SHTableCell>
    );
  },
);
